import { Fragment, useState, useEffect, useCallback, useRef } from 'react'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import api from 'services/api'
import EngineHeard, { MinuteHeard, TranscriptChip } from './EngineHeard'
import LedgerTable, {
  outcomeInk,
  outcomeOf,
  OutcomeChip,
} from '../Moments/LedgerTable'
import SpanCost from '../Moments/SpanCost'
import { MOMENT_OUTCOMES } from '../Moments/vocabulary'
import {
  DECISIONS,
  decisionOf,
  fmtCount,
  fmtMinutes,
  fmtPct,
  fmtSeconds,
} from './vocabulary'

// What the router heard, one account at a time.
//
// The flow above says the router is running and roughly right — a speech
// fraction near a tenth, a routed fraction near a quarter. Whether the voice
// detector is RIGHT is judged on audio somebody can listen to, which is their
// own: stream a day, come back, and read what was found minute by minute. So
// this defaults to `me` — the server resolves it to the signed-in staff
// account — and follows the page's user filter when it names someone else.
// The fleet-wide version is FleetSpans, which carries no account and so
// opens nobody's audio.
//
// Two reads. The list is off the span row (Mongo) and carries per segment the
// decision, both speech fractions, speech seconds and the flags, plus the
// span's classes ranked by minutes. Opening a span reads its forever object
// once (GCS) for the voice regions and every class above the floor, per
// segment. Nothing here is live: routing runs per span after it seals, so a
// span appears minutes after the recording ended and stays — until its owner
// deletes it from the span's own menu, which is the one write on this page.
const PAGE = 20
const CLASS_CHIPS = 6

const Legend = () => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
        mb: 1.5,
      }}
    >
      {Object.values(DECISIONS).map(({ label, color, help }) => (
        <Tooltip key={label} title={help} placement="top">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              cursor: 'default',
            }}
          >
            <Box
              sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color }}
            />
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Box>
        </Tooltip>
      ))}
      <Tooltip
        title="Each cell is a minute. Left half: how much Silero VAD called speech. Right half: YAMNet’s speech share. Either over 5% sends the minute to transcription."
        placement="top"
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            cursor: 'default',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: 22,
              height: 12,
              border: `1.5px solid ${DECISIONS.route.color}`,
              borderRadius: 0.5,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                flex: 1,
                bgcolor: alpha(DECISIONS.route.color, 0.7),
                borderRight: `1px solid ${theme.palette.background.paper}`,
              }}
            />
            <Box
              sx={{ flex: 1, bgcolor: alpha(DECISIONS.route.color, 0.25) }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            left fill VAD · right fill YAMNet speech
          </Typography>
        </Box>
      </Tooltip>
      <Tooltip
        title="A dot is a minute with another sound in it, like a dog, a train or music; the number is how many. Hover the cell for the loudest."
        placement="top"
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            cursor: 'default',
          }}
        >
          <Box
            sx={{
              minWidth: 14,
              height: 14,
              px: 0.4,
              borderRadius: 7,
              bgcolor: theme.palette.text.secondary,
              color: theme.palette.background.paper,
              fontSize: 9,
              fontWeight: 700,
              lineHeight: '14px',
              textAlign: 'center',
            }}
          >
            3
          </Box>
          <Typography variant="caption" color="text.secondary">
            other sounds YAMNet heard
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  )
}

// One class as a chip: its name and how long it was heard for.
const ClassChips = ({ classes, limit = CLASS_CHIPS, size = 'small' }) => {
  if (!classes || !classes.top?.length) return null
  const shown = classes.top.slice(0, limit)
  const rest = classes.top.length - shown.length + (classes.other?.classes || 0)
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {shown.map((entry) => (
        <Tooltip
          key={entry.index}
          title={`YAMNet class ${
            entry.index
          } · above the frame floor for ${fmtMinutes(entry.ms)}`}
          placement="top"
        >
          <Chip
            size={size}
            variant="outlined"
            label={`${entry.name} · ${fmtMinutes(entry.ms)}`}
            sx={{ fontSize: 11, height: 22 }}
          />
        </Tooltip>
      ))}
      {rest > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ alignSelf: 'center' }}
        >
          +{rest} more
        </Typography>
      )}
    </Box>
  )
}

// The strip: one cell per segment, in time order, width by duration. The
// outline is the decision. The cell is split in two: the left half fills with
// Silero VAD's speech fraction, the right with YAMNet's Speech-class frame
// fraction — the two inputs to the routing rule, side by side, so a routed
// minute with an empty left half is visibly YAMNet's rescue rather than a
// mystery. A badge in the corner counts the other classes YAMNet heard above
// the floor: a minute with no speech and a badge is an event, not silence.
//
// Once the span is selected, each moment the selector found is a bar along the
// bottom of the minutes it covers, in its outcome's ink (`marks`, the row's
// windows and outcomes). Clicking a bar plays that moment (`onMark`).
const Strip = ({ segments, height = 36, onPlay, playing, marks, onMark }) => {
  const theme = useTheme()
  const total = segments.reduce((sum, s) => sum + (s.duration_ms || 60_000), 0)
  const clamp = (value) => Math.max(0, Math.min(1, value || 0))
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '2px',
        height,
        width: '100%',
        alignItems: 'stretch',
      }}
    >
      {segments.map((segment) => {
        const decision = decisionOf(segment)
        const vad = clamp(segment.vad_speech_frac)
        const yam = clamp(segment.yamnet_speech_frac)
        const events = segment.event_classes || 0
        const isPlaying = playing === segment.name
        const segStart = segment.start_ms
        const segLength = segment.duration_ms || 60_000
        const found =
          segStart == null
            ? []
            : (marks || [])
                .map((mark, index) => ({ ...mark, index }))
                .filter(
                  (mark) =>
                    mark.start_ms < segStart + segLength &&
                    mark.end_ms > segStart
                )
        return (
          <Tooltip
            key={segment.name}
            placement="top"
            title={
              <Box sx={{ fontSize: 12 }}>
                <b>{dayjs(segment.start_ms).format('HH:mm:ss')}</b> ·{' '}
                {fmtSeconds((segment.duration_ms || 0) / 1000)} ·{' '}
                {decision.label}
                {onPlay && !segment.expired ? ' · click to listen' : ''}
                <br />
                VAD {fmtPct(segment.vad_speech_frac)} speech (
                {fmtSeconds((segment.speech_ms || 0) / 1000)}) · YAMNet{' '}
                {fmtPct(segment.yamnet_speech_frac)} speech frames
                {events > 0 ? (
                  <>
                    <br />
                    {events} other class{events === 1 ? '' : 'es'} heard
                    {segment.top_event
                      ? ` · loudest ${segment.top_event.name} ${fmtMinutes(
                          segment.top_event.ms
                        )}`
                      : ''}
                  </>
                ) : null}
                {segment.music_dominated ? <br /> : null}
                {segment.music_dominated ? 'music-dominated' : null}
                {segment.tv_present ? <br /> : null}
                {segment.tv_present ? 'television present' : null}
                {segment.media_score != null ? (
                  <>
                    <br />
                    media score {segment.media_score.toFixed(2)}
                  </>
                ) : null}
                {found.map((mark) => (
                  <Fragment key={mark.index}>
                    <br />
                    moment #{mark.index + 1} · {outcomeOf(mark.outcome).label}
                    {onMark ? ' · click its bar to hear it' : ''}
                  </Fragment>
                ))}
              </Box>
            }
          >
            <Box
              onClick={
                onPlay && !segment.expired
                  ? (event) => {
                      event.stopPropagation()
                      onPlay(segment)
                    }
                  : undefined
              }
              sx={{
                flex: `${(segment.duration_ms || 60_000) / total} 1 0`,
                minWidth: 10,
                position: 'relative',
                display: 'flex',
                border: `1.5px solid ${decision.color}`,
                borderStyle: segment.expired ? 'dashed' : 'solid',
                borderRadius: 1,
                bgcolor: alpha(decision.color, 0.06),
                overflow: 'hidden',
                cursor: onPlay && !segment.expired ? 'pointer' : 'default',
                boxShadow: isPlaying
                  ? `0 0 0 2px ${theme.palette.primary.main}`
                  : 'none',
                transition: 'box-shadow 150ms',
              }}
            >
              {[vad, yam].map((fill, half) => (
                <Box
                  key={half}
                  sx={{
                    flex: 1,
                    position: 'relative',
                    borderRight:
                      half === 0
                        ? `1px solid ${alpha(
                            theme.palette.background.paper,
                            0.9
                          )}`
                        : 'none',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: `${fill * 100}%`,
                      bgcolor: alpha(decision.color, half === 0 ? 0.75 : 0.45),
                      transition: 'height 300ms',
                    }}
                  />
                </Box>
              ))}
              {events > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 1,
                    right: 1,
                    minWidth: 13,
                    height: 13,
                    px: 0.35,
                    borderRadius: 7,
                    bgcolor: theme.palette.text.secondary,
                    color: theme.palette.background.paper,
                    fontSize: 9,
                    fontWeight: 700,
                    lineHeight: '13px',
                    textAlign: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  {events}
                </Box>
              )}
              {found.map((mark) => {
                const left = Math.max(0, (mark.start_ms - segStart) / segLength)
                const right = Math.min(1, (mark.end_ms - segStart) / segLength)
                return (
                  <Box
                    key={mark.index}
                    onClick={
                      onMark
                        ? (event) => {
                            event.stopPropagation()
                            onMark(mark)
                          }
                        : undefined
                    }
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      height: 5,
                      left: `${left * 100}%`,
                      width: `${Math.max(0, right - left) * 100}%`,
                      bgcolor: outcomeInk(theme, mark.outcome),
                      cursor: onMark ? 'pointer' : 'default',
                      zIndex: 1,
                    }}
                  />
                )
              })}
              {segment.ok === false && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: `repeating-linear-gradient(45deg, transparent 0 4px, ${alpha(
                      theme.palette.warning.main,
                      0.5
                    )} 4px 6px)`,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </Box>
          </Tooltip>
        )
      })}
    </Box>
  )
}

// The detail for one segment, off the forever object: Silero VAD's speech
// regions as a timeline, then every YAMNet class above the floor.
const fmtClock = (ms) => {
  const total = Math.round((ms || 0) / 1000)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

const fmtRuns = (runs) =>
  runs.map(([start, end]) => `${fmtClock(start)}–${fmtClock(end)}`).join(', ')

// The detail for one segment, off the forever object: Silero VAD's speech
// regions as a timeline, then every YAMNet class above the floor.
//
// When this minute is playing, the timeline IS the scrubber: a playhead moves
// along it, a click jumps the audio there, and hovering shows the time under
// the cursor. Clicking a class pins where YAMNet heard it (hover previews),
// drawn over the VAD regions — so "jump to the dog bark and listen" is two
// clicks. The native controls under the strip step aside while the detail is
// open; this is the better instrument.
const SegmentDetail = ({
  segment,
  onPlay,
  onSeek,
  playing,
  paused,
  position,
}) => {
  const theme = useTheme()
  const decision = decisionOf(segment)
  const duration = segment.duration_ms || 60_000
  const classes = segment.yamnet?.classes || []
  const [hovered, setHovered] = useState(null)
  const [pinned, setPinned] = useState(null)
  const [hoverMs, setHoverMs] = useState(null)
  const shown = hovered ?? pinned
  const shownRuns =
    shown != null
      ? classes.find((entry) => entry.index === shown)?.runs || []
      : []
  const isPlaying = playing === segment.name
  const playable = Boolean(onPlay) && !segment.expired
  const yamSpeech = segment.yamnet?.speech_runs // null = not recorded (v1)

  // What YAMNet heard at the playhead, other than speech or silence, held on
  // screen a beat after the run ends so a one-frame sigh is readable.
  const EVENT_LINGER_MS = 900
  const atPlayhead = isPlaying
    ? classes.filter(
        (entry) =>
          entry.runs &&
          !['Speech', 'Silence'].includes(entry.name) &&
          entry.runs.some(
            ([start, end]) =>
              position >= start && position < end + EVENT_LINGER_MS
          )
      )
    : []

  const fractionAt = (event) => {
    const rect = event.currentTarget.getBoundingClientRect()
    return Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '150px 1fr',
        gap: 1.5,
        alignItems: 'start',
        py: 0.75,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: isPlaying
          ? alpha(theme.palette.primary.main, 0.04)
          : 'transparent',
      }}
    >
      <Box>
        <Typography variant="caption" fontWeight={700} display="block">
          {segment.seg_start_ms
            ? dayjs(segment.seg_start_ms).format('HH:mm:ss')
            : segment.name}
        </Typography>
        <Typography
          variant="caption"
          display="block"
          sx={{ color: decision.color, fontWeight: 600 }}
        >
          {decision.label}
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ ml: 0.75 }}
          >
            {fmtSeconds(duration / 1000)}
          </Typography>
        </Typography>
        {playable && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              whiteSpace: 'nowrap',
            }}
          >
            <Button
              size="small"
              variant={isPlaying ? 'contained' : 'text'}
              disableElevation
              onClick={() => onPlay(segment)}
              sx={{
                px: 0.75,
                py: 0,
                minWidth: 0,
                minHeight: 0,
                fontSize: 11,
                lineHeight: 1.6,
                textTransform: 'none',
              }}
            >
              {!isPlaying ? '▶ listen' : paused ? '▶ resume' : '▮▮ pause'}
            </Button>
            {isPlaying && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {fmtClock(position)} / {fmtClock(duration)}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <Box>
        <Tooltip
          placement="top"
          followCursor
          title={
            hoverMs != null && playable
              ? `${fmtClock(hoverMs)} — click to ${
                  isPlaying ? 'jump here' : 'listen from here'
                }`
              : `Silero VAD: ${fmtSeconds(
                  (segment.vad?.speech_ms || 0) / 1000
                )} of speech in ${segment.vad?.regions?.length || 0} region${
                  segment.vad?.regions?.length === 1 ? '' : 's'
                } · level max ${segment.level?.max_dbfs ?? '—'} dBFS, rms ${
                  segment.level?.rms_dbfs ?? '—'
                } dBFS`
          }
        >
          {/* The track and its pop-ups share this frame: the pop-up sits
              above the track at the playhead's x, outside the clipped area. */}
          <Box sx={{ position: 'relative', pt: isPlaying ? 2.5 : 0, mb: 0.75 }}>
            {atPlayhead.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: `${((position || 0) / duration) * 100}%`,
                  transform: 'translateX(-50%)',
                  px: 0.75,
                  borderRadius: 1,
                  bgcolor: theme.palette.text.primary,
                  color: theme.palette.background.paper,
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: '18px',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              >
                {atPlayhead.map((entry) => entry.name).join(' · ')}
              </Box>
            )}
            <Box
              onClick={
                playable
                  ? (event) => onSeek(segment, fractionAt(event) * duration)
                  : undefined
              }
              onMouseMove={
                playable
                  ? (event) => setHoverMs(fractionAt(event) * duration)
                  : undefined
              }
              onMouseLeave={() => setHoverMs(null)}
              sx={{
                position: 'relative',
                height: isPlaying ? 22 : 14,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.text.primary, 0.06),
                overflow: 'hidden',
                cursor: playable ? 'pointer' : 'default',
                transition: 'height 150ms',
              }}
            >
              {/* Top band: Silero VAD's speech regions. Bottom band: YAMNet's
                Speech-class runs — the two routing signals, one above the
                other, the same split as the strip's cell halves. */}
              {(segment.vad?.regions || []).map(([start, end]) => (
                <Box
                  key={`vad-${start}-${end}`}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    height: yamSpeech ? '50%' : '100%',
                    left: `${(start / duration) * 100}%`,
                    width: `${Math.max(
                      0.5,
                      ((end - start) / duration) * 100
                    )}%`,
                    bgcolor: DECISIONS.route.color,
                  }}
                />
              ))}
              {(yamSpeech || []).map(([start, end]) => (
                <Box
                  key={`yam-${start}-${end}`}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    height: '50%',
                    left: `${(start / duration) * 100}%`,
                    width: `${Math.max(
                      0.5,
                      ((end - start) / duration) * 100
                    )}%`,
                    bgcolor: alpha(DECISIONS.route.color, 0.45),
                  }}
                />
              ))}
              {shownRuns.map(([start, end]) => (
                <Box
                  key={`run-${start}-${end}`}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${(start / duration) * 100}%`,
                    width: `${Math.max(
                      0.8,
                      ((end - start) / duration) * 100
                    )}%`,
                    bgcolor: alpha(theme.palette.primary.main, 0.85),
                    outline: `1px solid ${theme.palette.background.paper}`,
                  }}
                />
              ))}
              {hoverMs != null && playable && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${(hoverMs / duration) * 100}%`,
                    width: '1px',
                    bgcolor: alpha(theme.palette.text.primary, 0.5),
                    pointerEvents: 'none',
                  }}
                />
              )}
              {isPlaying && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${((position || 0) / duration) * 100}%`,
                    width: '2px',
                    bgcolor: theme.palette.text.primary,
                    boxShadow: `0 0 0 1px ${theme.palette.background.paper}`,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </Box>
          </Box>
        </Tooltip>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
          }}
        >
          {segment.error && (
            <Chip
              size="small"
              color="warning"
              label={segment.error}
              sx={{ fontSize: 11, height: 22 }}
            />
          )}
          {classes.map((entry) => {
            const active = shown === entry.index
            return (
              <Tooltip
                key={entry.index}
                placement="top"
                title={`${fmtPct(
                  entry.frac
                )} of frames above the floor · peak score ${entry.max ?? '—'}${
                  entry.runs == null
                    ? ' · times not recorded before router v2'
                    : entry.runs.length
                    ? ` · at ${fmtRuns(entry.runs)} — click to pin`
                    : ''
                }`}
              >
                <Chip
                  size="small"
                  variant={pinned === entry.index ? 'filled' : 'outlined'}
                  color={pinned === entry.index ? 'primary' : 'default'}
                  label={`${entry.name} · ${fmtMinutes(entry.ms)}`}
                  onMouseEnter={() => setHovered(entry.index)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={
                    entry.runs && entry.runs.length
                      ? () =>
                          setPinned((current) =>
                            current === entry.index ? null : entry.index
                          )
                      : undefined
                  }
                  sx={{
                    fontSize: 11,
                    height: 22,
                    borderColor: active
                      ? theme.palette.primary.main
                      : undefined,
                  }}
                />
              </Tooltip>
            )
          })}
          {segment.yamnet?.classes_heard > classes.length && (
            <Typography variant="caption" color="text.secondary">
              +{segment.yamnet.classes_heard - classes.length} more
            </Typography>
          )}
          {segment.expired && (
            <Typography variant="caption" color="text.secondary">
              audio was gone before the router ran
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

// One player for the whole section. Exactly one <audio> element exists at a
// time, rendered under whichever span is playing; starting a minute anywhere
// replaces it, so two spans can never sound at once. It holds the segment
// being heard (span + name), its object URL, where to start, the playhead,
// and the policy's answer when it said no. The strip's cells, the native
// controls and the detail's timeline are three faces of this one element.
//
// A found moment's window (step 4a) plays through the same element: from the
// window's start, on into the next minute when it runs over, and paused at its
// end. `untilRef` holds that end; any other play or seek clears it.
const useOnePlayer = (userId) => {
  const audioRef = useRef(null)
  const untilRef = useRef(null)
  const [playing, setPlaying] = useState(null)
  const [paused, setPaused] = useState(false)
  const [position, setPosition] = useState(0)
  const [error, setError] = useState(null)

  // Revoke a replaced URL; the element that used it is gone by then.
  useEffect(
    () => () => {
      if (playing?.url) URL.revokeObjectURL(playing.url)
    },
    [playing]
  )

  const stop = useCallback(() => {
    untilRef.current = null
    setPlaying(null)
    setPaused(false)
    setPosition(0)
    setError(null)
  }, [])

  const load = async (span, segment, atMs) => {
    setError(null)
    try {
      const blob = await api.streamMetrics.getRoutingSegment({
        userId,
        spanStartMs: span.start_ms,
        name: segment.name,
      })
      setPosition(atMs || 0)
      setPaused(false)
      setPlaying({
        span: span.start_ms,
        name: segment.name,
        start_ms: segment.start_ms ?? segment.seg_start_ms,
        url: URL.createObjectURL(blob),
        seekTo: atMs || 0,
      })
    } catch (err) {
      setPlaying(null)
      setError({ span: span.start_ms, message: err.message })
    }
  }

  const isCurrent = (span, segment) =>
    playing?.span === span.start_ms && playing?.name === segment.name

  // The listen button and a strip cell: start, or pause / resume the one
  // already loaded.
  const play = async (span, segment) => {
    untilRef.current = null
    const audio = audioRef.current
    if (isCurrent(span, segment) && audio) {
      if (audio.paused) audio.play().catch(() => {})
      else audio.pause()
      return
    }
    await load(span, segment, 0)
  }

  // The timeline: jump within the minute that is playing, or start another
  // minute from that point.
  const seek = async (span, segment, ms) => {
    untilRef.current = null
    const audio = audioRef.current
    if (isCurrent(span, segment) && audio) {
      audio.currentTime = ms / 1000
      setPosition(ms)
      if (audio.paused) audio.play().catch(() => {})
      return
    }
    await load(span, segment, ms)
  }

  // A found moment, from its start to its end. A start in a minute that never
  // landed begins at the next minute that did.
  const playWindow = async (span, startMs, endMs) => {
    const segment =
      span.segments.find(
        (s) =>
          s.start_ms != null &&
          s.start_ms <= startMs &&
          startMs < s.start_ms + s.duration_ms
      ) || span.segments.find((s) => s.start_ms != null && s.start_ms > startMs)
    if (!segment || segment.start_ms >= endMs) return
    untilRef.current = null
    await load(span, segment, Math.max(0, startMs - segment.start_ms))
    untilRef.current = endMs
  }

  // The element's `timeupdate`: the playhead, and a window's end.
  const tick = (seconds) => {
    const ms = seconds * 1000
    setPosition(ms)
    const until = untilRef.current
    if (until != null && playing && playing.start_ms + ms >= until) {
      untilRef.current = null
      audioRef.current?.pause()
    }
  }

  // The element's `ended`: a window that runs past this minute goes on into
  // the next one; anything else stops.
  const ended = (span) => {
    const until = untilRef.current
    const next =
      until != null && playing
        ? span.segments.find(
            (s) =>
              s.start_ms != null && s.start_ms > playing.start_ms && !s.expired
          )
        : null
    if (next && next.start_ms < until) {
      load(span, next, 0)
      return
    }
    untilRef.current = null
    setPlaying(null)
  }

  return {
    audioRef,
    playing,
    paused,
    position,
    error,
    play,
    seek,
    playWindow,
    tick,
    ended,
    stop,
    setPosition,
    setPaused,
    setPlaying,
  }
}

// The span's own menu, top right. One item: erase the span for good — the
// audio, the router record with every YAMNet class, the row. The server allows
// it on the caller's own spans and on nothing else, with no setting that widens
// it, so the item is offered everywhere and the server's refusal is shown in
// its own words where the row's other errors go.
const SpanMenu = ({ span, onErase, erasing }) => {
  const [anchor, setAnchor] = useState(null)
  const [confirming, setConfirming] = useState(false)
  const stop = (event) => event.stopPropagation()
  return (
    <Box onClick={stop} sx={{ ml: -0.5, alignSelf: 'center' }}>
      <Tooltip title="Span options" placement="top">
        <IconButton
          size="small"
          aria-label="span options"
          disabled={erasing}
          onClick={(event) => setAnchor(event.currentTarget)}
          sx={{ p: 0.25 }}
        >
          {erasing ? (
            <CircularProgress size={14} />
          ) : (
            <MoreVertIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          dense
          onClick={() => {
            setAnchor(null)
            setConfirming(true)
          }}
          sx={{ color: 'error.main', fontSize: 13 }}
        >
          Delete span…
        </MenuItem>
      </Menu>
      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle sx={{ fontSize: 16 }}>
          Delete the span from{' '}
          {dayjs(span.start_ms).format('ddd MMM D · HH:mm')}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            Its audio, the router's record of it with every class YAMNet heard,
            and the span itself are removed, and it leaves every figure on this
            page within a minute. Only your own spans can be deleted. The
            buckets keep a 7-day soft delete, so an operator can still restore
            it for a week; after that it is gone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            disableElevation
            onClick={() => {
              setConfirming(false)
              onErase()
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// The selection chip (step 4a), beside the transcript's, off the span row:
// what the selector found and the span's cost — transcription plus selection,
// the same number the Moments page states. The tooltip names every outcome.
const usd = (value) => `$${value.toFixed(4)}`

const SelectionChip = ({ selection, transcription }) => {
  if (!selection) {
    if (transcription?.status !== 'transcribed') return null
    return (
      <Chip
        size="small"
        variant="outlined"
        label="not selected yet"
        sx={{ height: 20, fontSize: 11 }}
      />
    )
  }
  const found = selection.moments.length
  const cards = selection.outcomes.cut || 0
  const cost =
    selection.cost_usd != null
      ? ` · ${selection.transcript_missing ? '≥ ' : ''}${usd(
          selection.cost_usd
        )}`
      : ''
  let label = `${found} found · ${cards} card${cards === 1 ? '' : 's'}`
  let color = cards ? 'success' : 'default'
  if (selection.terminal || selection.state === 'failed') {
    label = `selection ${selection.state}`
    color = 'error'
  } else if (!selection.selected) {
    label = selection.state === 'running' ? 'selecting' : 'selection queued'
  } else if (!found) {
    label = 'nothing found'
  }
  const tally = MOMENT_OUTCOMES.filter((o) => selection.outcomes[o.key])
    .map((o) => `${o.label} ${selection.outcomes[o.key]}`)
    .join(' · ')
  const tip = [
    tally,
    selection.reason,
    selection.last_error && `last error: ${selection.last_error}`,
    selection.cost_usd != null &&
      `span cost: transcription plus selection, over ${selection.claims} pass${
        selection.claims === 1 ? '' : 'es'
      }${
        selection.transcript_missing
          ? '; the transcript doc is gone (30 days), so transcription is not counted'
          : ''
      }`,
  ]
    .filter(Boolean)
    .join(' · ')
  const chip = (
    <Chip
      size="small"
      variant="outlined"
      color={color}
      label={`${label}${cost}`}
      sx={{ height: 20, fontSize: 11 }}
    />
  )
  return tip ? (
    <Tooltip title={tip} placement="top">
      {chip}
    </Tooltip>
  ) : (
    chip
  )
}

// What the selector found in an opened span (step 4a): the span's cost and
// times, then its ledger — every moment, its quote, the judge's bar and the
// outcome — each row playing its window from the span's own minutes, refused
// ones included (they have no cut, so this is the only way to hear one). Read
// once, on the span's first open; the ledger follows the Moments page's rule,
// so another staff member's on production answers 404 here.
const SelectorFound = ({ span, userId, player, canPlay }) => {
  const [ledger, setLedger] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    let live = true
    api.streamMetrics
      .getSelectionSpan({ userId, spanStartMs: span.start_ms })
      .then((data) => live && setLedger(data))
      .catch((err) => live && setError(err.message))
    return () => {
      live = false
    }
  }, [userId, span.start_ms])
  const rows = ledger?.job?.moments || []
  const tally = rows.reduce((counts, row) => {
    const key = row.outcome || 'owed'
    return { ...counts, [key]: (counts[key] || 0) + 1 }
  }, {})
  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.75,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
          What the selector found
        </Typography>
        {MOMENT_OUTCOMES.filter((o) => tally[o.key]).map((o) => (
          <OutcomeChip key={o.key} outcome={o.key} count={tally[o.key]} />
        ))}
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        Each found moment is a bar under the minutes it covers and a band on its
        words above, in its outcome's colour; # is its row here.
        {canPlay ? ' ▶ plays its window from the minutes.' : ''}
      </Typography>
      {error && (
        <Typography variant="caption" color="text.secondary">
          {error}
        </Typography>
      )}
      {!ledger && !error && <CircularProgress size={16} />}
      {ledger && <SpanCost ledger={ledger} />}
      {ledger && !ledger.job?.selected && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Nothing selected yet
          {ledger.job?.reason ? `: ${ledger.job.reason}` : ''}.
        </Typography>
      )}
      {ledger?.job?.selected && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The selector found nothing worth a card
          {ledger.job.reason ? ` (${ledger.job.reason})` : ''}.
        </Typography>
      )}
      {rows.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <LedgerTable
            rows={rows}
            zone={ledger.timezone}
            onPlay={
              canPlay
                ? (row) => player.playWindow(span, row.start_ms, row.end_ms)
                : undefined
            }
          />
        </Box>
      )}
    </Box>
  )
}

// Audio lives 30 days; a moment older than that has no minutes left to play.
const AUDIO_DAYS = 30
const NO_MOMENTS = []

// `isTarget`: the span a link from the Moments page named. It opens itself and
// scrolls into view, once on mount and again when its record lands (the fleet
// panels above load in their own time and push the page down).
const SpanRow = ({ span, userId, player, onErased, isTarget }) => {
  const [open, setOpen] = useState(Boolean(isTarget))
  // The selector's section mounts on the first open and stays: its ledger, and
  // the trace read behind it, are fetched once.
  const [selectorSeen, setSelectorSeen] = useState(Boolean(isTarget))
  const rowRef = useRef(null)
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [erasing, setErasing] = useState(false)
  const [eraseError, setEraseError] = useState(null)
  const [transcript, setTranscript] = useState(null)
  const [transcriptLoading, setTranscriptLoading] = useState(false)
  const [transcriptError, setTranscriptError] = useState(null)

  // This row's view of the one player: the segment playing here, if any.
  const playing = player.playing?.span === span.start_ms ? player.playing : null
  const playError =
    player.error?.span === span.start_ms ? player.error.message : null
  const { paused, position } = player

  // The span's record, fetched once — on expand, or on the first minute
  // played from the strip, because the minute's detail row is the player.
  const ensureDetail = async () => {
    if (detail || loading) return
    setLoading(true)
    try {
      setDetail(
        await api.streamMetrics.getRoutingSpan({
          userId,
          spanStartMs: span.start_ms,
        })
      )
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const play = (segment) => {
    ensureDetail()
    ensureTranscript()
    return player.play(span, segment)
  }
  const seek = (segment, ms) => {
    ensureDetail()
    ensureTranscript()
    return player.seek(span, segment, ms)
  }

  // What the engine heard (step 2b): read once — on expand, or on the first
  // minute played, whose words show under it — and only when the row says
  // there is a transcript to read; every other status is on the chip
  // already, off the span row.
  // Any answered run means a doc, whatever the span now waits on (late
  // audio, an outage): its words stay readable.
  const hasTranscript =
    Boolean(span.transcription?.has_doc) ||
    ['transcribed', 'terminal'].includes(span.transcription?.status)
  const ensureTranscript = async () => {
    if (!hasTranscript || transcript || transcriptLoading) return
    setTranscriptLoading(true)
    try {
      setTranscript(
        await api.streamMetrics.getRoutingTranscript({
          userId,
          spanStartMs: span.start_ms,
        })
      )
      setTranscriptError(null)
    } catch (err) {
      setTranscriptError(err.message)
    } finally {
      setTranscriptLoading(false)
    }
  }

  const toggle = async () => {
    const next = !open
    setOpen(next)
    if (next) {
      setSelectorSeen(true)
      await Promise.all([ensureDetail(), ensureTranscript()])
    }
  }

  const scrollHere = () =>
    rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  useEffect(() => {
    if (!isTarget) return
    scrollHere()
    ensureDetail()
    ensureTranscript()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isTarget && detail) scrollHere()
  }, [isTarget, detail])

  const audioKept = span.start_ms >= Date.now() - AUDIO_DAYS * 86_400_000
  const marks = span.selection?.moments || NO_MOMENTS
  const playMark = audioKept
    ? (mark) => player.playWindow(span, mark.start_ms, mark.end_ms)
    : undefined

  // Collapsed, with a minute playing: that one minute's detail row stands in
  // for the player — its timeline is the scrubber, its button pauses — as if
  // the span were expanded to exactly the row that was clicked.
  const focused =
    !open && playing
      ? detail?.segments.find((segment) => segment.name === playing.name)
      : null

  // Erase, then drop the row: the server's answer is the truth of what is
  // left, so the row goes only once it says so. A minute of this span that
  // was sounding stops first — the element under it is about to unmount.
  const erase = async () => {
    setErasing(true)
    setEraseError(null)
    try {
      await api.streamMetrics.deleteRoutingSpan({
        userId,
        spanStartMs: span.start_ms,
      })
      if (playing) player.stop()
      onErased(span.start_ms)
    } catch (err) {
      setEraseError(err.message)
      setErasing(false)
    }
  }

  const totals = span.totals
  const wall = (span.end_ms - span.start_ms) / 1000
  return (
    <Box
      ref={rowRef}
      sx={{
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 'none' },
        scrollMarginTop: 80,
      }}
    >
      <Box
        onClick={toggle}
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 2,
          cursor: 'pointer',
          mb: 1,
        }}
      >
        <Tooltip
          placement="top"
          title={`start_ms ${span.start_ms} · sealed ${
            span.sealed_at || '—'
          } · routed ${span.routed_at || '—'} · router v${
            span.router_version
          } · params ${span.params_id}${
            span.seal_cause ? ` · closed on ${span.seal_cause}` : ''
          }`}
        >
          <Typography variant="body2" fontWeight={700}>
            {dayjs(span.start_ms).format('ddd MMM D · HH:mm')}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 0.75 }}
            >
              {fmtSeconds(wall)}
            </Typography>
          </Typography>
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          <b>{fmtPct(totals.speech_fraction)}</b> speech ·{' '}
          <b>{fmtPct(totals.routed_fraction)}</b> routed ·{' '}
          {fmtCount(span.segments_total)} segment
          {span.segments_total === 1 ? '' : 's'}
          {span.segments_expired ? ` · ${span.segments_expired} expired` : ''}
          {span.routing_error ? ' · last pass failed' : ''}
        </Typography>
        <TranscriptChip
          transcription={span.transcription}
          transcript={transcript}
        />
        <SelectionChip
          selection={span.selection}
          transcription={span.transcription}
        />
        <Box sx={{ flex: 1 }} />
        <ClassChips classes={span.classes} />
        <SpanMenu span={span} onErase={erase} erasing={erasing} />
      </Box>
      {eraseError && (
        <Typography
          variant="caption"
          color="error.main"
          display="block"
          sx={{ mb: 1 }}
        >
          {eraseError}
        </Typography>
      )}
      <Strip
        segments={span.segments}
        onPlay={play}
        playing={playing?.name}
        marks={marks}
        onMark={playMark}
      />
      {playing && (
        // The one element that plays. Never shown: the minute's detail row —
        // below when collapsed, in the list when expanded — is the player.
        <audio
          key={playing.url}
          ref={player.audioRef}
          src={playing.url}
          autoPlay
          style={{ display: 'none' }}
          onLoadedMetadata={(event) => {
            if (playing.seekTo)
              event.currentTarget.currentTime = playing.seekTo / 1000
          }}
          onTimeUpdate={(event) => player.tick(event.currentTarget.currentTime)}
          onPlay={() => player.setPaused(false)}
          onPause={() => player.setPaused(true)}
          onEnded={() => player.ended(span)}
        />
      )}
      {playError && (
        <Typography
          variant="caption"
          color="error.main"
          display="block"
          sx={{ mt: 1 }}
        >
          {playError}
        </Typography>
      )}
      {!open && playing && (
        <Box sx={{ mt: 1 }} onClick={(event) => event.stopPropagation()}>
          {focused ? (
            <>
              <SegmentDetail
                segment={focused}
                onPlay={play}
                onSeek={seek}
                playing={playing.name}
                paused={paused}
                position={position}
              />
              <MinuteHeard
                segment={focused}
                span={span}
                transcript={transcript}
                loading={transcriptLoading}
                error={transcriptError}
                playing={playing}
                position={position}
                onSeek={seek}
              />
            </>
          ) : error ? (
            <Typography variant="caption" color="error.main">
              playing {dayjs(playing.start_ms).format('HH:mm:ss')} · the
              minute's record could not be read: {error}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary">
                playing {dayjs(playing.start_ms).format('HH:mm:ss')} · reading
                the minute's record
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <Collapse in={open}>
        <Box sx={{ mt: 1.5 }}>
          {loading && <CircularProgress size={16} />}
          {error && (
            <Typography variant="caption" color="error.main">
              {error}
            </Typography>
          )}
          {detail && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 0.5 }}
              >
                From the router's record of this span. Each minute's timeline
                carries Silero VAD's speech regions on top and YAMNet's speech
                beneath, then every YAMNet class heard above the floor. Click a
                timeline to listen from there; click a class to pin where it was
                heard; while playing, what YAMNet hears pops up over the
                playhead.
                {detail.params?.vad_floor != null
                  ? ` Floors in force: VAD ${fmtPct(
                      detail.params.vad_floor
                    )}, YAMNet speech ${fmtPct(
                      detail.params.yamnet_speech_floor
                    )}.`
                  : ''}
              </Typography>
              {detail.segments.map((segment) => (
                <Box key={segment.name}>
                  <SegmentDetail
                    segment={segment}
                    onPlay={play}
                    onSeek={seek}
                    playing={playing?.name}
                    paused={paused}
                    position={playing?.name === segment.name ? position : 0}
                  />
                  {playing?.name === segment.name && (
                    <MinuteHeard
                      segment={segment}
                      span={span}
                      transcript={transcript}
                      loading={transcriptLoading}
                      error={transcriptError}
                      playing={playing}
                      position={position}
                      onSeek={seek}
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}
          {hasTranscript && (
            <EngineHeard
              span={span}
              transcript={transcript}
              loading={transcriptLoading}
              error={transcriptError}
              playing={playing}
              position={position}
              onSeek={seek}
              moments={marks}
            />
          )}
          {selectorSeen && span.selection && (
            <SelectorFound
              span={span}
              userId={userId}
              player={player}
              canPlay={audioKept}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

// `focus`: a span's `start_ms` named by a link (from a moment's card, or from a
// day on the Users page, which names the day's last span). The list
// then starts at that span (keyset: everything before `focus + 1`), the span
// opens and scrolls into view, and "Show newest" (`onClearFocus`) goes back to
// the top of the account's list.
const RouterHeard = ({ user, focus, onClearFocus }) => {
  const [rows, setRows] = useState([])
  const [cursor, setCursor] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // `me` is the server's word for the signed-in staff account. The page's
  // filter, when set, names somebody else.
  const userId = user?.id || 'me'
  const player = useOnePlayer(userId)

  const load = useCallback(
    async (beforeStartMs) => {
      setLoading(true)
      try {
        const data = await api.streamMetrics.getRouting({
          userId,
          limit: PAGE,
          beforeStartMs,
        })
        setRows((prev) =>
          beforeStartMs == null ? data.rows : [...prev, ...data.rows]
        )
        setCursor(data.next_before_start_ms)
        setError(null)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    },
    [userId]
  )

  useEffect(() => {
    setRows([])
    setCursor(null)
    load(focus != null ? focus + 1 : null)
  }, [load, focus])

  // A new account's list must not keep the old account's minute sounding.
  const { stop } = player
  useEffect(() => stop, [userId, stop])

  const whose = user ? user.username || user.id : 'you'

  if (error) {
    return (
      <Typography variant="body2" color="error.main">
        {error}
      </Typography>
    )
  }

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {loading
          ? 'Loading…'
          : user
          ? 'The router has finished no spans for this account yet.'
          : 'The router has finished none of your spans yet. Stream a day and come back — a span appears here minutes after it seals, and stays.'}
      </Typography>
    )
  }

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        Routed spans for <b>{whose}</b>, newest first. Click a span for the
        minute-by-minute record; click a minute to listen to it.
      </Typography>
      {focus != null && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {rows[0]?.start_ms === focus
              ? `From the span the link named (${dayjs(focus).format(
                  'ddd MMM D · HH:mm'
                )}) back.`
              : `The span the link named (${dayjs(focus).format(
                  'ddd MMM D · HH:mm'
                )}) is not among the routed spans; older ones follow.`}
          </Typography>
          <Button size="small" onClick={onClearFocus}>
            Show newest
          </Button>
        </Box>
      )}
      <Legend />
      {rows.map((span) => (
        <SpanRow
          key={span.start_ms}
          span={span}
          userId={userId}
          isTarget={focus != null && span.start_ms === focus}
          player={player}
          onErased={(startMs) =>
            setRows((prev) => prev.filter((row) => row.start_ms !== startMs))
          }
        />
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={!cursor || loading}
          onClick={() => load(cursor)}
        >
          {cursor ? 'Load older' : 'No older spans'}
        </Button>
        {loading && <CircularProgress size={16} />}
        <Typography variant="caption" color="text.secondary">
          {fmtCount(rows.length)} span{rows.length === 1 ? '' : 's'}
        </Typography>
      </Box>
    </Box>
  )
}

export default RouterHeard
